import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, CreditCard, Plus, Loader2, Edit, Trash2,
  CheckCircle2, XCircle, Smartphone, Banknote, Send,
  Globe, Code, DollarSign, Wallet,
} from "lucide-react";
import { pasarelasApi, PasarelaPago, TipoPasarela } from "../../lib/api";
import { useInicio } from "../../lib/permisos";
import { EditarPasarelaModal } from "./EditarPasarelaModal";

const TIPOS: Array<{ tipo: TipoPasarela; nombre: string; icon: React.ReactNode; descripcion: string }> = [
  { tipo: "pago_movil", nombre: "Pago Móvil", icon: <Smartphone className="size-5" />, descripcion: "Banco + cédula + teléfono" },
  { tipo: "transferencia", nombre: "Transferencia bancaria", icon: <Banknote className="size-5" />, descripcion: "Banco + cuenta + beneficiario" },
  { tipo: "zelle", nombre: "Zelle", icon: <Send className="size-5" />, descripcion: "Email/teléfono Zelle" },
  { tipo: "paypal", nombre: "PayPal", icon: <Wallet className="size-5" />, descripcion: "Email PayPal o API checkout" },
  { tipo: "stripe", nombre: "Stripe", icon: <CreditCard className="size-5" />, descripcion: "Tarjeta de crédito/débito" },
  { tipo: "mercadopago", nombre: "Mercado Pago", icon: <CreditCard className="size-5" />, descripcion: "Pasarela MP" },
  { tipo: "enlace_pago", nombre: "Enlace de pago", icon: <Globe className="size-5" />, descripcion: "URL de cualquier proveedor" },
  { tipo: "api_custom", nombre: "API personalizada", icon: <Code className="size-5" />, descripcion: "Endpoint propio" },
  { tipo: "efectivo", nombre: "Efectivo", icon: <DollarSign className="size-5" />, descripcion: "Pago en el local o contra entrega" },
];

export function PasarelasPagoPage() {
  const inicio = useInicio();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PasarelaPago | null>(null);
  const [creating, setCreating] = useState<TipoPasarela | null>(null);

  const lista = useQuery({
    queryKey: ["pasarelas"],
    queryFn: () => pasarelasApi.list(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => pasarelasApi.eliminar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pasarelas"] }),
  });

  const toggleActiva = useMutation({
    mutationFn: ({ id, activa }: { id: number; activa: boolean }) =>
      pasarelasApi.actualizar(id, { activa } as any),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pasarelas"] }),
  });

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-6xl px-6 py-8 animate-fade-in">

        <Link to={inicio}
          className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800 transition-colors mb-5">
          <ArrowLeft className="size-4" />
          Dashboard
        </Link>

        <header className="flex items-end justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-sm">
              <CreditCard className="size-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-ink-800 leading-tight">Pasarelas de pago</h1>
              <p className="text-sm text-ink-500 mt-0.5">
                Configura métodos de pago para delivery y otros canales
              </p>
            </div>
          </div>
        </header>

        {/* Lista pasarelas configuradas */}
        <div className="card mb-5">
          <div className="px-5 py-3 border-b border-ink-200 flex items-center justify-between">
            <h2 className="font-display font-bold text-ink-800">Pasarelas configuradas</h2>
            {lista.data && (
              <span className="text-xs text-ink-500">{lista.data.length} configurada{lista.data.length !== 1 ? "s" : ""}</span>
            )}
          </div>
          {lista.isLoading ? (
            <div className="p-8 text-center text-ink-400">
              <Loader2 className="size-5 animate-spin mx-auto" />
            </div>
          ) : !lista.data || lista.data.length === 0 ? (
            <div className="p-12 text-center text-ink-400">
              <CreditCard className="size-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay pasarelas configuradas todavía</p>
              <p className="text-xs mt-1">Agrega una abajo</p>
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {lista.data.map((p) => {
                const tipo = TIPOS.find((t) => t.tipo === p.tipo);
                return (
                  <div key={p.id} className="px-5 py-3 flex items-center gap-4 hover:bg-brand-50/30">
                    <div className={`size-10 rounded-lg flex items-center justify-center shrink-0
                      ${p.activa ? "bg-brand-50 text-brand-600" : "bg-ink-100 text-ink-400"}`}>
                      {tipo?.icon || <CreditCard className="size-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink-800">{p.nombre}</span>
                        {p.modo === "sandbox" && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">SANDBOX</span>
                        )}
                        {p.confirmacionAutomatica && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">AUTO</span>
                        )}
                      </div>
                      <div className="text-xs text-ink-500">{tipo?.nombre || p.tipo} · {p.moneda}</div>
                    </div>
                    <button onClick={() => toggleActiva.mutate({ id: Number(p.id), activa: !p.activa })}
                      className={`text-xs font-medium px-2.5 py-1 rounded transition-colors
                        ${p.activa ? "bg-success/10 text-success hover:bg-success/20" : "bg-ink-100 text-ink-500 hover:bg-ink-200"}`}>
                      {p.activa ? <><CheckCircle2 className="size-3 inline mr-1" />Activa</> : <><XCircle className="size-3 inline mr-1" />Inactiva</>}
                    </button>
                    <button onClick={() => setEditing(p)}
                      className="p-1.5 rounded hover:bg-brand-100 text-brand-700">
                      <Edit className="size-4" />
                    </button>
                    <button onClick={() => {
                      if (confirm(`¿Eliminar "${p.nombre}"?`)) deleteMut.mutate(Number(p.id));
                    }} className="p-1.5 rounded hover:bg-red-100 text-red-600">
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Agregar nueva */}
        <div className="card">
          <div className="px-5 py-3 border-b border-ink-200 flex items-center gap-2">
            <Plus className="size-4 text-brand-600" />
            <h2 className="font-display font-bold text-ink-800">Agregar nueva pasarela</h2>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {TIPOS.map((t) => (
              <button key={t.tipo} onClick={() => setCreating(t.tipo)}
                className="text-left p-4 rounded-lg border border-ink-200 hover:border-brand-400 hover:bg-brand-50/40 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="size-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                    {t.icon}
                  </div>
                  <div className="font-medium text-ink-800 text-sm">{t.nombre}</div>
                </div>
                <div className="text-xs text-ink-500">{t.descripcion}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Modal editar */}
        <EditarPasarelaModal
          pasarela={editing}
          nuevoTipo={creating}
          open={!!editing || !!creating}
          onClose={() => { setEditing(null); setCreating(null); }}
        />
      </div>
    </div>
  );
}
